/**
 * Locale-sensitive formatting via Intl. Display ≠ conversion: formatMoney renders the amount in the
 * currency it is denominated in (source currency, default INR); converting is a separate, explicit
 * step (`convertMoney`) that needs a validated rate from GET /api/regions/fx.
 */
const COUNTRY_DEFAULTS = {
  IN: { currency: "INR", tz: "Asia/Kolkata", dial: "+91", groups: [5, 5], langs: ["en", "hi", "ta"] },
  US: { currency: "USD", tz: "America/New_York", dial: "+1", groups: [3, 3, 4] },
  GB: { currency: "GBP", tz: "Europe/London", dial: "+44", groups: [4, 6] },
  AE: { currency: "AED", tz: "Asia/Dubai", dial: "+971", groups: [2, 3, 4] },
  FR: { currency: "EUR", tz: "Europe/Paris", dial: "+33", groups: [1, 2, 2, 2, 2] },
};
let REGISTRY = {};

export function setCountryRegistry(list) {
  REGISTRY = Object.fromEntries((list || []).map((c) => [c.code, {
    currency: c.currency, tz: c.default_timezone, dial: c.dial_code, groups: c.phone_groups, address: c.address_format,
    langs: c.format_languages,
  }]));
}

export function readPref() {
  try { return JSON.parse(localStorage.getItem("f2n_locale_pref") || "{}"); }
  catch { return {}; }
}

export function countryInfo(code) {
  const c = (code || "IN").toUpperCase();
  return REGISTRY[c] || COUNTRY_DEFAULTS[c] || COUNTRY_DEFAULTS.IN;
}

export function resolveLocale({ lang, country, tz } = {}) {
  const pref = readPref();
  const c = (country || pref.country || "IN").toUpperCase();
  const info = countryInfo(c);
  const l = (lang || pref.lang || "en").toLowerCase().split("-")[0];
  // Number/date conventions follow the LANGUAGE; the country region applies only where that
  // language natively uses it (en-IN lakh grouping, fr-FR…). en + FR stays English formatting.
  const native = (info.langs || []).includes(l);
  const bare = l === "en" && c !== "US" ? "en-GB" : l;   // international English: day-month, 24h
  return { ...info, country: c, locale: native ? `${l}-${c}` : bare, tz: tz || pref.tz || info.tz };
}

export function formatMoney(amount, opts = {}) {
  if (amount == null || amount === "") return "";
  const { locale } = resolveLocale(opts);
  const currency = opts.currency || "INR";
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency", currency, maximumFractionDigits: currency === "JPY" ? 0 : 2,
    }).format(Number(amount));
  } catch { return `${currency} ${amount}`; }
}

/** Returns null when no validated rate is available — callers must then show the source amount. */
export function convertMoney(amount, fx) {
  if (!fx || !fx.available || !Number.isFinite(Number(amount))) return null;
  return { amount: Number(amount) * fx.rate, currency: fx.quote, as_of: fx.fetched_at, indicative: true };
}

export function formatNumber(value, opts = {}) {
  if (value == null || value === "") return "";
  try { return new Intl.NumberFormat(resolveLocale(opts).locale).format(Number(value)); }
  catch { return String(value); }
}

export function formatDate(iso, opts = {}) {
  if (!iso) return "";
  const { locale, tz } = resolveLocale(opts);
  try {
    return new Intl.DateTimeFormat(locale, { day: "2-digit", month: "short", year: "numeric", timeZone: tz })
      .format(new Date(iso));
  } catch { return iso; }
}

export function formatTime(iso, opts = {}) {
  if (!iso) return "";
  const { locale, tz } = resolveLocale(opts);
  try {
    return new Intl.DateTimeFormat(locale, { hour: "2-digit", minute: "2-digit", timeZone: tz, timeZoneName: opts.showTz ? "short" : undefined })
      .format(new Date(iso));
  } catch { return iso; }
}

export function formatDateTime(iso, opts = {}) {
  return `${formatDate(iso, opts)} · ${formatTime(iso, opts)}`;
}

export function formatRelative(iso, opts = {}) {
  if (!iso) return "";
  const sec = Math.round((new Date(iso).getTime() - Date.now()) / 1000);
  try {
    const rtf = new Intl.RelativeTimeFormat(resolveLocale(opts).locale, { numeric: "auto" });
    const abs = Math.abs(sec);
    if (abs >= 86400) return rtf.format(Math.round(sec / 86400), "day");
    if (abs >= 3600) return rtf.format(Math.round(sec / 3600), "hour");
    if (abs >= 60) return rtf.format(Math.round(sec / 60), "minute");
    return rtf.format(sec, "second");
  } catch { return iso; }
}

/** National number grouped per the country registry, prefixed with the dial code. */
export function formatPhone(raw, opts = {}) {
  const { dial, groups } = resolveLocale(opts);
  let d = String(raw || "").replace(/[^\d]/g, "");
  const dialDigits = (dial || "").replace("+", "");
  if (dialDigits && d.startsWith(dialDigits) && d.length > (groups || []).reduce((a, b) => a + b, 0)) d = d.slice(dialDigits.length);
  d = d.replace(/^0+/, "");
  if (!d) return "";
  const parts = [];
  let i = 0;
  for (const g of groups || [d.length]) { if (i >= d.length) break; parts.push(d.slice(i, i + g)); i += g; }
  if (i < d.length) parts.push(d.slice(i));
  return `${dial} ${parts.join(" ")}`;
}

/** Address lines ordered per country (`address_format` from the registry). */
export function formatAddress(a = {}, opts = {}) {
  const { address } = resolveLocale(opts);
  const order = address || ["line1", "line2", "city", "state", "postal_code", "country"];
  const v = {
    ...a,
    city_state_postal: [a.city, [a.state, a.postal_code].filter(Boolean).join(" ")].filter(Boolean).join(", "),
    postal_city: [a.postal_code, a.city].filter(Boolean).join(" "),
  };
  return order.map((k) => v[k]).filter(Boolean);
}

export function saveLocalePref(patch) {
  try {
    const cur = readPref();
    const next = { ...cur };
    for (const [k, v] of Object.entries(patch || {})) if (v !== undefined && v !== null) next[k] = v;
    localStorage.setItem("f2n_locale_pref", JSON.stringify(next));
  } catch { /* noop */ }
}
